import { useState, useCallback } from 'react';
import ConfirmDialog from '../components/Common/ConfirmDialog';

interface ConfirmOptions {
  message: string;
  confirmText?: string;
  cancelText?: string;
}

/**
 * Hook that replaces window.confirm() with the styled ConfirmDialog component.
 * Usage:
 *   const { confirm, ConfirmDialogNode } = useConfirmDialog();
 *   // In JSX: {ConfirmDialogNode}
 *   // In handler: if (await confirm('Sure?')) { doAction(); }
 */
const useConfirmDialog = () => {
  const [pending, setPending] = useState<{
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const confirm = useCallback((message: string, options?: Partial<ConfirmOptions>): Promise<boolean> => {
    return new Promise((resolve) => {
      setPending({ options: { message, ...options }, resolve });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    pending?.resolve(true);
    setPending(null);
  }, [pending]);

  const handleCancel = useCallback(() => {
    pending?.resolve(false);
    setPending(null);
  }, [pending]);

  const ConfirmDialogNode = pending ? (
    <ConfirmDialog
      message={pending.options.message}
      confirmText={pending.options.confirmText ?? 'Taip'}
      cancelText={pending.options.cancelText ?? 'Atšaukti'}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  ) : null;

  return { confirm, ConfirmDialogNode };
};

export default useConfirmDialog;
